import { EnServiceDetailPage } from "@/components/pages/ServiceDetailPageEn";
// Dynamic EN service detail page — mirrors /services/$service in English.
// Generates one indexable page per ServiceId (29 pages), with full SEO + JSON-LD.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import {
  Phone,
  MessageCircle,
  Mail,
  CheckCircle2,
  ArrowRight,
  Clock,
  Globe2,
  MapPin,
  ShieldCheck,
  Star,
} from "lucide-react";
import type { ServiceId } from "@/data/services";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { getReviewerForService, attorneyPersonSchema } from "@/data/service-reviewers";

const SITE = "https://nyclanguageinstitute.com";


export const Route = createFileRoute("/en/services/$service/")({
  loader: async ({ params }) => {
    const { SERVICE_HEAD, resolveServiceIdLight } = await import("@/data/services-head.gen");
    const id = resolveServiceIdLight(params.service) as ServiceId | undefined;
    if (!id) throw notFound();
    return { service: id, meta: SERVICE_HEAD[id] };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return { meta: [{ title: ctaTitle("Service not found", "/en/services/$service", "en") }] };
    const s = loaderData.meta;
    const enUrl = `${SITE}/en/services/${s.slug}`;
    const thUrl = `${SITE}/services/${s.slug}`;
    const title = `${s.nameEn} | NYC Language Institute Thailand`;
    const desc = `${s.taglineEn} · ${s.turnaround} · 50+ languages · Reply in 15 min. Get your checklist in minutes.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/services/$service", "en") },
        {
          name: "description",
          content: ctaDesc(desc.slice(0, 158), "/en/services/$service", "en"),
        },
        { name: "keywords", content: s.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/services/$service", "en") },
        {
          property: "og:description",
          content: ctaDesc(desc.slice(0, 158), "/en/services/$service", "en"),
        },
        { property: "og:url", content: enUrl },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "th_TH" },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: enUrl },
        ...hreflangLinks(`/services/${s.slug}`, `/en/services/${s.slug}`),
      ],
      scripts: [
        speakableSchema(),
        consolidateGraph([
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${enUrl}#service`,
              name: s.nameEn,
              description: s.taglineEn,
              url: enUrl,
              inLanguage: "en",
              provider: {
                "@type": "ProfessionalService",
                name: "NYC Legal & Notary Service Co., Ltd.",
                url: `${SITE}/en`,
                telephone: "+66-83-249-4999",
                email: "contact@nyclegal.co.th",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "61 Soi Ladprao 95 (Prangthip), Wang Thonglang",
                  addressLocality: "Bangkok",
                  postalCode: "10310",
                  addressCountry: "TH",
                },
              },
              areaServed: { "@type": "Country", name: "Thailand" },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "THB",
                lowPrice: s.priceFrom,
                highPrice: s.priceTo,
                availability: "https://schema.org/InStock",
              },
              reviewedBy: attorneyPersonSchema(getReviewerForService(loaderData.service), SITE),
              sameAs: [thUrl],
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${enUrl}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home (EN)", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/en/services` },
                { "@type": "ListItem", position: 3, name: s.nameEn, item: enUrl },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: EnServiceDetailPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Service not found</h1>
      <Link to="/en/services" className="mt-4 inline-block text-amber-300 hover:underline">
        ← Back to services
      </Link>
    </main>
  ),
});
